import { groupBy } from 'lodash';
import { useSelector } from 'react-redux';
import { MenuItem, Menu, Fade } from '@mui/material';
import { useDebounce } from 'use-debounce';
import type { ScenarioDto } from '@/api-gen/api.schemas';
import type { FC } from 'react';
import React, { useMemo, useState, useCallback } from 'react';

import useStyles from '../styles.ts';
import { scenarioGroupCreateGroup, scenarioGroupAddScenarioToGroup, scenarioGroupRemoveScenesFromGroup } from '@/api-gen/api.ts';
import useBoolState from 'app/hooks/useBoolState.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import useCursorPosition from 'app/components/NavigationDrawer/hooks/useCursorPosition.ts';
import NavigationDrawerCompanyScenarios from 'app/components/NavigationDrawer/ScenarioSection/NavigationDrawerScenarioList/NavigationDrawerCompanyScenarios.tsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getScenarioGroupsQuery } from 'app/queries/scenarioQueries.ts';
import type { SceneGroupWithScenes, SceneWithCompanyName, NavigationDrawerClasses } from './types.ts';

import { useTranslation } from 'react-i18next';
import type { I18nScenarioSectionNs } from '@/i18n/dictionaries/interfaces.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { useRouteMatch } from 'react-router';
import AppRoutes from 'app/utils/routes.ts';

export interface NavigationDrawerScenarioListProps {
  filterScenes: SceneWithCompanyName[];
  handleSelectScenario: () => void;
  isShowGroupsEnabled: boolean;
  searchWord: string;
}

const sortScenes = (scenes: SceneWithCompanyName[]): SceneWithCompanyName[] => {
  return scenes.sort((a, b) => {
    const companyComparison = (a.companyName || '').localeCompare(b.companyName || '');
    if (companyComparison !== 0) return companyComparison;

    const aGroupName = a.groupName || '';
    const bGroupName = b.groupName || '';
    const groupComparison = aGroupName.localeCompare(bGroupName);
    if (groupComparison !== 0) return groupComparison;

    return a.name.localeCompare(b.name);
  });
};

const NavigationDrawerScenarioList: FC<NavigationDrawerScenarioListProps> = ({
  searchWord,
  filterScenes,
  handleSelectScenario,
  isShowGroupsEnabled
}): JSX.Element => {
  const classes = useStyles() as NavigationDrawerClasses;

  const [translate] = useTranslation(I18nNamespace.ScenarioSection);
  const isUserAnyAdmin = useSelector(UserSelectors.isUserAnyAdminSelector);
  const isUserSuperAdmin = useSelector(UserSelectors.isUserSuperAdminSelector);
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);

  const currentScenario = useSelector(ScenarioSelectors.currentScenario);

  const queryClient = useQueryClient();
  const sceneGroupsQueryData = useQuery(getScenarioGroupsQuery);
  const sceneGroups = useMemo(() => sceneGroupsQueryData.data ?? [], [sceneGroupsQueryData.data]);

  const sceneRouteMatch = useRouteMatch({
    path: AppRoutes.Scene,
    strict: false
  });

  const sceneId = useMemo(() => {
    if (!sceneRouteMatch) {
      return null;
    }
    return currentScenario?.sceneId;
  }, [currentScenario, location.pathname]);

  const [lastRightClickedScene, setLastRightClickedScene] = React.useState<ScenarioDto>();

  // Track which companies are expanded
  const [openCompanies, setOpenCompanies] = useState<number[]>([]);

  const handleClickOnSubmenu = useCallback((_key: number) => {
    setOpenCompanies((prev) => (prev.includes(_key) ? prev.filter((id) => id !== _key) : [...prev, _key]));
  }, []);

  // Track which scene groups are expanded
  const [openSceneGroups, setOpenSceneGroups] = useState<number[]>([]);

  const handleClickOnGroupSubmenu = useCallback((_key: number) => {
    setOpenSceneGroups((prev) => (prev.includes(_key) ? prev.filter((id) => id !== _key) : [...prev, _key]));
  }, []);

  const scenesByCompanyId = useMemo(() => {
    const sortedScenes = sortScenes(filterScenes);
    const group = groupBy(sortedScenes, (el: SceneWithCompanyName) => {
      return el.companyName;
    });
    return Object.values(group);
  }, [filterScenes]);

  const sceneIdsInCompanyGroups = useMemo(() => {
    return Object.entries(groupBy(sceneGroups, (sg) => sg.companyId)).map(([id, sgArr]) => ({
      companyId: id,
      sceneIds: sgArr.flatMap((sg) => sg.sceneIds)
    }));
  }, [sceneGroups]);

  const groupMoveMenuRef = React.useRef<HTMLLIElement>(null);
  const groupRemoveMenuRef = React.useRef<HTMLLIElement>(null);
  const [groupMoveMenuOpen, openGroupMoveMenu, closeGroupMoveMenu] = useBoolState();

  const [selectedScenes, setSelectedScenes] = useState<number[]>([]);
  const [isGroupAddEnabled, showAddGroup, closeAddGroup] = useBoolState();

  const { cursorPosition, setCursorPosition, resetCursorPosition } = useCursorPosition();

  const getGroupsWithScenes = useCallback<(scenes: ScenarioDto[], companyId: number) => SceneGroupWithScenes[]>(
    (scenes: ScenarioDto[], companyId: number) => {
      return sceneGroups
        .filter((x) => x.companyId == companyId)
        .map((sg): SceneGroupWithScenes => {
          const sceneGroupScenes = scenes
            .filter(
              (scene) =>
                (companyId == scene.companyId || scene.sharedCompanies?.some((sc) => sc.id === companyId)) &&
                sg.sceneIds?.includes(scene.id)
            )
            .sort((a, b) => a.name.localeCompare(b.name));
          return {
            id: sg.id,
            title: sg.title,
            companyId: sg.companyId,
            scenes: sceneGroupScenes,
            sceneIds: sg.sceneIds || [],
            disableActions: false
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title));
    },
    [sceneGroups]
  );

  const isRemoveFromGroupDisabled = useMemo(() => {
    if (!lastRightClickedScene) return true;

    return !sceneGroups.some(
      (x) =>
        (x.companyId === lastRightClickedScene.companyId ||
          lastRightClickedScene.sharedCompanies?.some((sc) => sc.id === x.companyId)) &&
        x.sceneIds.includes(lastRightClickedScene.id)
    );
  }, [lastRightClickedScene, sceneGroups]);

  const handleRightClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, scene: ScenarioDto) => {
      if (!isUserAnyAdmin) return;

      event.preventDefault();
      setCursorPosition({
        x: event.clientX - 2,
        y: event.clientY - 4
      });
      setSelectedScenes([scene.id]);
      setLastRightClickedScene(scene);
    },
    [isUserAnyAdmin, setCursorPosition]
  );

  const handleCloseMenu = useCallback(() => {
    closeGroupMoveMenu();
    resetCursorPosition();
  }, [closeGroupMoveMenu, resetCursorPosition]);

  const createSceneGroup = useCallback(
    async (groupName: string, companyId?: number): Promise<SceneGroupWithScenes> => {
      const response = await scenarioGroupCreateGroup(groupName, companyId ? { companyId } : undefined);
      queryClient.invalidateQueries(getScenarioGroupsQuery.queryKey);
      closeAddGroup();

      return {
        ...response.data,
        scenes: [], // New groups start with no scenes
        sceneIds: response.data.sceneIds || []
      };
    },
    [closeAddGroup, queryClient]
  );

  const moveScenesToGroup = useCallback(
    async (groupId: number, selectedSceneIds: number[]) => {
      await scenarioGroupAddScenarioToGroup(groupId, { sceneIds: selectedSceneIds });
      queryClient.invalidateQueries(getScenarioGroupsQuery.queryKey);
      handleCloseMenu();
    },
    [handleCloseMenu, queryClient]
  );

  const removeScenesFromGroup = useCallback(
    async (groupId: number, selectedSceneIds: number[]) => {
      await scenarioGroupRemoveScenesFromGroup(groupId, { sceneIds: selectedSceneIds });
      queryClient.invalidateQueries(getScenarioGroupsQuery.queryKey);
      handleCloseMenu();
    },
    [handleCloseMenu, queryClient]
  );

  const contextMenu = (sceneGroupId?: number) => {
    const companySceneGroups = sceneGroups.filter(
      (x) => x.companyId === (lastRightClickedScene?.companyId ?? currentUser.companyId)
    );

    return (
      <>
        <Menu
          keepMounted
          TransitionComponent={Fade}
          open={cursorPosition.y !== null}
          onClose={handleCloseMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            cursorPosition.y !== null && cursorPosition.x !== null
              ? { top: cursorPosition.y, left: cursorPosition.x }
              : undefined
          }
        >
          <MenuItem onClick={openGroupMoveMenu} ref={groupMoveMenuRef}>
            {translate(nameof.full<I18nScenarioSectionNs>((l) => l.movetoFolder))}
          </MenuItem>
          <MenuItem
            ref={groupRemoveMenuRef}
            disabled={isRemoveFromGroupDisabled || !sceneGroupId}
            onClick={() => removeScenesFromGroup(sceneGroupId, [lastRightClickedScene.id])}
          >
            {translate(nameof.full<I18nScenarioSectionNs>((l) => l.removeFromFolder))}
          </MenuItem>
        </Menu>
        <Menu
          keepMounted
          open={groupMoveMenuOpen}
          onClose={handleCloseMenu}
          PaperProps={{
            onMouseLeave: () => {
              closeGroupMoveMenu();
            }
          }}
          anchorEl={groupMoveMenuRef.current}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'top'
          }}
          transformOrigin={{
            horizontal: 'left',
            vertical: 'top'
          }}
        >
          {!isGroupAddEnabled && (
            <MenuItem
              divider={!!companySceneGroups.length}
              onClick={() => {
                handleCloseMenu();
                showAddGroup();
              }}
            >
              {translate(nameof.full<I18nScenarioSectionNs>((l) => l.createNew))}
            </MenuItem>
          )}
          {companySceneGroups.map((sceneGroup) => (
            <MenuItem
              key={'moveToGroup' + sceneGroup.id}
              onClick={() => moveScenesToGroup(sceneGroup.id, selectedScenes)}
            >
              {sceneGroup.title}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const onCreateSceneGroup = useCallback(
    async (sceneGroupName) => {
      const sceneGroup = await createSceneGroup(sceneGroupName, lastRightClickedScene?.companyId);

      if (!sceneGroup) return;
      if (!lastRightClickedScene) return;

      await moveScenesToGroup(sceneGroup.id, [lastRightClickedScene.id]);
    },
    [createSceneGroup, lastRightClickedScene, moveScenesToGroup]
  );

  const hasMultipleCompanies = scenesByCompanyId.length > 1;

  // Debounce the incoming searchWord so we only respond after user stops typing
  const [debouncedSearchWord] = useDebounce(searchWord, 300);
  const prevDebouncedSearchWord = React.useRef<string>('');

  React.useEffect(() => {
    // only proceed if the search term actually changed
    if (prevDebouncedSearchWord.current === debouncedSearchWord) {
      return;
    }
    prevDebouncedSearchWord.current = debouncedSearchWord;

    // Only auto-expand if user is showing groups, we have filtered scenes, and search is non-empty
    if (
      !isShowGroupsEnabled ||
      !filterScenes ||
      filterScenes.length === 0 ||
      (debouncedSearchWord?.trim().length ?? 0) < 3
    ) {
      return;
    }

    // Expand all companies which contain the filtered scenes
    const newOpenCompanies = new Set<number>(openCompanies);
    filterScenes.forEach((scene) => {
      // Expand the company if the scene belongs to it (including shared scenarios with companyId: 0)
      if (scene.companyId !== null && scene.companyId !== undefined) {
        newOpenCompanies.add(scene.companyId);
      }
      // Expand any shared companies as well
      scene.sharedCompanies?.forEach((shared) => {
        newOpenCompanies.add(shared.id);
      });
    });

    // Expand groups that contain these scenes
    const newOpenGroups = new Set<number>(openSceneGroups);

    // Helper: Recursively open all parent groups that include a given scene
    function openAllParentGroups(groups: SceneGroupWithScenes[], sceneId: number): boolean {
      let foundScene = false;
      groups.forEach((group) => {
        let groupContainsScene = group.sceneIds?.includes(sceneId) ?? false;

        // Recursively check subGroups
        if (group.subGroups && group.subGroups.length > 0) {
          const subGroupContainsScene = openAllParentGroups(group.subGroups, sceneId);
          if (subGroupContainsScene) {
            groupContainsScene = true;
          }
        }

        // If this group (or any subGroup) contains the scene, mark it, and open
        if (groupContainsScene) {
          newOpenGroups.add(group.id);
          foundScene = true;
        }
      });
      return foundScene;
    }

    // For each filtered scene, identify the relevant group(s) and open them
    filterScenes.forEach((scene) => {
      sceneGroups.forEach((group) => {
        // Only check groups relevant to this scene's company or shared companies
        if (
          group.companyId === scene.companyId ||
          scene.sharedCompanies?.some((shared) => shared.id === group.companyId)
        ) {
          openAllParentGroups([group], scene.id);
        }
      });
    });

    // Convert sets into arrays for comparison
    const newOpenCompaniesArr = Array.from(newOpenCompanies);
    const newOpenGroupsArr = Array.from(newOpenGroups);

    // Check if openCompanies or openSceneGroups actually changed
    const companiesChanged =
      newOpenCompaniesArr.length !== openCompanies.length ||
      newOpenCompaniesArr.some((id) => !openCompanies.includes(id)) ||
      openCompanies.some((id) => !newOpenCompaniesArr.includes(id));

    const groupsChanged =
      newOpenGroupsArr.length !== openSceneGroups.length ||
      newOpenGroupsArr.some((id) => !openSceneGroups.includes(id)) ||
      openSceneGroups.some((id) => !newOpenGroupsArr.includes(id));

    if (companiesChanged) {
      setOpenCompanies(newOpenCompaniesArr);
    }

    if (groupsChanged) {
      setOpenSceneGroups(newOpenGroupsArr);
    }
  }, [filterScenes, isShowGroupsEnabled, sceneGroups, openCompanies, openSceneGroups, debouncedSearchWord]);

  return (
    <>
      {scenesByCompanyId.map((scenes: SceneWithCompanyName[]) => {
        const initialScene = scenes.find((scene) => scene.companyId !== null) || scenes[0];

        let companyId = currentUser.companyId;

        if (isUserSuperAdmin) {
          companyId = initialScene?.companyId;
        }

        // Determine if this company is expanded
        const open = openCompanies.includes(companyId);

        let groupsWithScenes = getGroupsWithScenes(scenes, companyId);

        const sceneIdsInGroupsForCompany =
          sceneIdsInCompanyGroups.find((obj) => obj.companyId === companyId?.toString())?.sceneIds ?? [];

        if (!companyId) {
          const mapping = new Map<number, { title: string; sceneIds: number[] }>();

          if (isUserSuperAdmin) {
            for (const scene of scenes) {
              if (scene.sharedCompanies?.length) {
                for (const shared of scene.sharedCompanies) {
                  const found = mapping.get(shared.id);

                  if (found) found.sceneIds.push(scene.id);
                  else mapping.set(shared.id, { title: shared.name, sceneIds: [scene.id] });
                }
              }
            }
          }

          groupsWithScenes = Array.from(mapping).map(([companyId, { title, sceneIds }], index) => {
            const finalScenes = new Set(sceneIds);

            const subGroups: SceneGroupWithScenes[] = [];
            const filteredGroups = sceneGroups.filter((x) => x.companyId === companyId);
            for (const group of filteredGroups) {
              const scenesToAdd = [];
              for (const sceneId of sceneIds) {
                if (group.sceneIds?.includes(sceneId)) {
                  scenesToAdd.push(sceneId);
                  finalScenes.delete(sceneId);
                }
              }

              if (scenesToAdd.length) {
                subGroups.push({
                  id: group.id,
                  title: group.title,
                  companyId: group.companyId,
                  scenes: scenes.filter((x) => scenesToAdd.includes(x.id)).sort((a, b) => a.name.localeCompare(b.name)),
                  sceneIds: scenesToAdd,
                  disableActions: true
                });
              }
            }

            return {
              scenes: scenes.filter((x) => finalScenes.has(x.id)).sort((a, b) => a.name.localeCompare(b.name)),
              companyId,
              id: -1 - index,
              sceneIds: Array.from(finalScenes),
              title,
              subGroups: subGroups.sort((a, b) => a.title.localeCompare(b.title)),
              disableActions: true
            };
          });
        }

        const ungroupedScenes = scenes
          .filter((scene) => !sceneIdsInGroupsForCompany.includes(scene.id))
          .sort((a, b) => a.name.localeCompare(b.name));

        return (
          <NavigationDrawerCompanyScenarios
            key={companyId}
            openCompanies={openCompanies}
            scenes={ungroupedScenes}
            companyId={companyId}
            initialScene={initialScene}
            open={hasMultipleCompanies ? open : true}
            handleClickOnSubmenu={handleClickOnSubmenu}
            isShowGroupsEnabled={isShowGroupsEnabled}
            groupsWithScenes={groupsWithScenes}
            classes={classes}
            sceneId={sceneId}
            openSceneGroups={openSceneGroups}
            contextMenu={contextMenu}
            handleRightClick={handleRightClick}
            handleSelectScenario={handleSelectScenario}
            isGroupAddEnabled={isGroupAddEnabled}
            closeAddGroup={closeAddGroup}
            onCreateSceneGroup={onCreateSceneGroup}
            sceneIdsInGroups={sceneIdsInGroupsForCompany}
            handleClickOnGroupSubmenu={handleClickOnGroupSubmenu}
            displayCompanyName={hasMultipleCompanies}
          />
        );
      })}
    </>
  );
};

export default NavigationDrawerScenarioList;
